/**
 * Silhouettes — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_illustration`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const silhouettesPack: ToilePack = {
    slug: 'silhouettes',
    category: 'illustration',
    name: 'Silhouettes',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'stag', label: 'Stag', subject: 'a stag standing alert with a full spread of antlers, head raised and one foreleg forward'},
        {key: 'dancer', label: 'Dancer', subject: 'a dancer mid-leap with one arm thrown overhead, back arched and a skirt flaring behind'},
        {key: 'skyline', label: 'Skyline', subject: 'a city skyline of towers, spires and rooftops with a bridge span carrying off one end'},
    ],
    styles: [
        {key: 'solid', label: 'Solid', style: 'a single solid black fill with no interior detail at all, the closed contour carrying the whole drawing', palette: 'Black ink only, one flat value edge to edge', framing: 'the shape centered with an even white margin on all four sides'},
        {key: 'cutfile', label: 'Cut file', style: 'a vinyl cut file, one welded shape with thick sturdy connections, no floating islands and every notch wide enough for a blade', palette: 'Black ink only, a single cut layer', framing: 'the shape centered and filling most of the square, its outline crisp at full size'},
        {key: 'papercut', label: 'Papercut', style: 'a papercut stencil, the solid shape held together by narrow bridges with slim cut-away slivers reading the interior', palette: 'Black ink only, the white ground standing in for every cut', framing: 'the shape centered with a clear white margin, the bridges kept wide enough to survive a blade'},
    ],
    directions: ['a running horse instead', 'a pine forest line', 'as a matching pair facing each other'],
    seo: {
        title: 'Silhouette images: stag, dancer, skyline',
        metaDescription: 'Nine silhouette images — a stag, a dancer and a city skyline — drawn solid, as a welded cut file and as a bridged papercut, black on white.',
        h1: 'Silhouette images: a stag, a dancer and a city skyline',
        tagline: 'Nine silhouettes, three subjects, three cut styles — solid, cut file, papercut.',
        intro: 'A silhouette is a solid black shape on white, the edge carrying the whole drawing — vinyl decal, papercut or stencil ready. These nine cut a stag, a leaping dancer and a city skyline as a solid fill, a welded cut file with sturdy bridges and a bridged papercut stencil. Ask for a different subject and the same three cut styles draw it.',
        useCases: [
            'a vinyl decal for a car or laptop',
            'a stencil for painting or a mural',
            'a papercut craft project',
            'a wall art print',
        ],
        faq: [
            {q: 'Are these ready-to-cut SVG files?', a: 'No — each image is a flat raster silhouette; trace or convert it to a vector in your cutting software before sending it to a blade.'},
            {q: 'What size do these print at?', a: 'Each silhouette is a square 1024×1024 canvas with an even margin, so it scales cleanly to a decal, a stencil or a full-page papercut.'},
            {q: 'Can I get a different subject?', a: 'Yes — the direction field takes any subject, such as a running horse or a pine forest, and the same three cut styles draw it.'},
        ],
    },
}
